import React, { useState, useEffect } from 'react';
import { Competition, competitionService, SinglePoolConfig, DEFAULT_SINGLE_POOL_CONFIG } from '../../lib/services/competitionService';
import { referralSyncService } from '../../lib/services/referralSyncService';

interface InvestmentPhase {
  id: number;
  phase_number: number;
  phase_name: string;
  price_per_share: number;
  is_active: boolean;
  total_shares_available: number;
  shares_sold: number;
}

interface TierConfigForm {
  tier_level: number;
  tier_name: string;
  tier_emoji: string;
  volume_min: number;
  volume_max: number | null;
  pool_allocation_percent: number;
  min_direct_buyers: number;
}

interface NewCompetitionForm {
  name: string;
  description: string;
  phaseId: number;
  totalPrizePool: number;
  minimumQualification: number;
  prizes: {
    first: number;
    second: number;
    third: number;
    fourth_to_tenth: number;
  };
  tierConfigs: TierConfigForm[];
}

const DEFAULT_TIER_CONFIGS: TierConfigForm[] = [
  { tier_level: 1, tier_name: 'Prospectors', tier_emoji: '⛏️', volume_min: 500, volume_max: 4999, pool_allocation_percent: 15, min_direct_buyers: 0 },
  { tier_level: 2, tier_name: 'Miners', tier_emoji: '🪨', volume_min: 5000, volume_max: 19999, pool_allocation_percent: 25, min_direct_buyers: 0 },
  { tier_level: 3, tier_name: 'Diggers', tier_emoji: '💎', volume_min: 20000, volume_max: 59999, pool_allocation_percent: 30, min_direct_buyers: 3 },
  { tier_level: 4, tier_name: 'Master Diggers', tier_emoji: '👑', volume_min: 60000, volume_max: null, pool_allocation_percent: 30, min_direct_buyers: 5 },
];

const PhaseCompetitionManager: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const [investmentPhases, setInvestmentPhases] = useState<InvestmentPhase[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'phases' | 'competitions' | 'leaderboard'>('phases');
  const [editingTierCompetitionId, setEditingTierCompetitionId] = useState<string | null>(null);
  const [editTierConfigs, setEditTierConfigs] = useState<TierConfigForm[]>([]);
  const [_savingTierConfig, setSavingTierConfig] = useState(false);
  // Single Pool Config (Phase 5+)
  const [editingSinglePoolId, setEditingSinglePoolId] = useState<string | null>(null);
  const [singlePoolConfig, setSinglePoolConfig] = useState<SinglePoolConfig>({ ...DEFAULT_SINGLE_POOL_CONFIG });
  const [savingSinglePoolConfig, setSavingSinglePoolConfig] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCompetition, setNewCompetition] = useState<NewCompetitionForm>({
    name: '',
    description: 'Build your network by referring new shareholders. Each qualified referral counts toward your ranking.',
    phaseId: 0,
    totalPrizePool: 150000,
    minimumQualification: 2500,
    prizes: {
      first: 60000,
      second: 30000,
      third: 18000,
      fourth_to_tenth: 6000
    },
    tierConfigs: [...DEFAULT_TIER_CONFIGS]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load investment phases via API
      const phases = await competitionService.loadPhases();
      setInvestmentPhases(phases || []);

      // Load competitions via API
      const comps = await competitionService.getAllCompetitions();
      setCompetitions(comps || []);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      await referralSyncService.syncReferralData();
      await loadData();
    } catch (error) {
      console.error('Error syncing data:', error);
    } finally {
      setSyncing(false);
    }
  };

  const __handleEditTierConfig = async (competitionId: string) => {
    if (editingTierCompetitionId === competitionId) {
      setEditingTierCompetitionId(null);
      setEditTierConfigs([]);
      return;
    }
    try {
      const configs = await competitionService.getTierConfig(competitionId);
      if (configs.length > 0) {
        setEditTierConfigs(configs.map(c => ({
          tier_level: c.tier_level,
          tier_name: c.tier_name,
          tier_emoji: c.tier_emoji || (c as any).emoji || '',
          volume_min: c.volume_min,
          volume_max: c.volume_max,
          pool_allocation_percent: c.pool_allocation_percent,
          min_direct_buyers: c.min_direct_buyers,
        })));
      } else {
        setEditTierConfigs([...DEFAULT_TIER_CONFIGS]);
      }
      setEditingTierCompetitionId(competitionId);
    } catch (error) {
      console.error('Error loading tier config:', error);
    }
  };

  const __handleSaveTierConfig = async () => {
    if (!editingTierCompetitionId) return;
    const totalAlloc = editTierConfigs.reduce((s, t) => s + t.pool_allocation_percent, 0);
    if (totalAlloc !== 100) {
      alert(`Pool allocation must equal 100%. Currently: ${totalAlloc}%`);
      return;
    }
    setSavingTierConfig(true);
    try {
      const success = await competitionService.saveTierConfig(editingTierCompetitionId, editTierConfigs.map(tc => ({
        tier_level: tc.tier_level,
        tier_name: tc.tier_name,
        tier_emoji: tc.tier_emoji,
        emoji: tc.tier_emoji,
        volume_min: tc.volume_min,
        volume_max: tc.volume_max,
        pool_allocation_percent: tc.pool_allocation_percent,
        min_direct_buyers: tc.min_direct_buyers,
      })));
      if (success) {
        alert('Tier configuration saved successfully!');
        setEditingTierCompetitionId(null);
        setEditTierConfigs([]);
      } else {
        alert('Failed to save tier configuration.');
      }
    } catch (error) {
      console.error('Error saving tier config:', error);
      alert('Error saving tier configuration.');
    } finally {
      setSavingTierConfig(false);
    }
  };

  // ===== Single Pool Config Handlers (Phase 5+) =====
  const handleEditSinglePoolConfig = async (competitionId: string) => {
    if (editingSinglePoolId === competitionId) {
      setEditingSinglePoolId(null);
      return;
    }
    try {
      const config = await competitionService.getSinglePoolConfig(competitionId);
      setSinglePoolConfig(config ? { ...DEFAULT_SINGLE_POOL_CONFIG, ...config } : { ...DEFAULT_SINGLE_POOL_CONFIG });
      setEditingSinglePoolId(competitionId);
    } catch (error) {
      console.error('Error loading single pool config:', error);
    }
  };

  const handleSaveSinglePoolConfig = async () => {
    if (!editingSinglePoolId) return;
    setSavingSinglePoolConfig(true);
    try {
      const success = await competitionService.saveSinglePoolConfig(editingSinglePoolId, singlePoolConfig);
      if (success) {
        alert('Single Pool configuration saved successfully!');
        setEditingSinglePoolId(null);
      } else {
        alert('Failed to save configuration.');
      }
    } catch (error) {
      console.error('Error saving single pool config:', error);
      alert('Error saving configuration.');
    } finally {
      setSavingSinglePoolConfig(false);
    }
  };

  const handleDeleteCompetition = async (competitionId: string, competitionName: string) => {
    if (!confirm(`Are you sure you want to delete "${competitionName}"?\n\nThis will permanently remove the competition and all its prize tiers. This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete competition + tier configs + prize tiers via API
      const success = await competitionService.deleteCompetition(competitionId);
      if (!success) throw new Error('Delete failed');

      alert(`Competition "${competitionName}" has been deleted successfully.`);
      await loadData();

    } catch (error) {
      console.error('Error deleting competition:', error);
      alert('Error deleting competition. Please try again.');
    }
  };

  const handleCreateCompetition = async () => {
    if (!newCompetition.name || !newCompetition.phaseId) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const phase = investmentPhases.find(p => p.id === newCompetition.phaseId);
      if (!phase) {
        alert('Selected phase not found');
        return;
      }

      // v4.2.1: Phase 5+ uses Top 50 percentage-based prizes; Phase 4 and earlier use Top 10 dollar amounts
      const isTop50Phase = phase.phase_number >= 5;
      let prizeTiers;
      let totalPrizePool;

      if (isTop50Phase) {
        // Top 50: percentage-based tiers — prize_amount stores the percentage, actual $ calculated at runtime
        const pct = DEFAULT_SINGLE_POOL_CONFIG.payout_percentages;
        prizeTiers = [
          { tier_name: '1st Place', tier_rank_start: 1, tier_rank_end: 1, prize_amount: pct[1], display_order: 1, emoji: '🥇' },
          { tier_name: '2nd Place', tier_rank_start: 2, tier_rank_end: 2, prize_amount: pct[2], display_order: 2, emoji: '🥈' },
          { tier_name: '3rd Place', tier_rank_start: 3, tier_rank_end: 3, prize_amount: pct[3], display_order: 3, emoji: '🥉' },
          { tier_name: '4th–10th Place', tier_rank_start: 4, tier_rank_end: 10, prize_amount: pct['4-10'], display_order: 4, emoji: '🏆' },
          { tier_name: '11th–20th Place', tier_rank_start: 11, tier_rank_end: 20, prize_amount: pct['11-20'], display_order: 5, emoji: '🏅' },
          { tier_name: '21st–30th Place', tier_rank_start: 21, tier_rank_end: 30, prize_amount: pct['21-30'], display_order: 6, emoji: '🎖️' },
          { tier_name: '31st–50th Place', tier_rank_start: 31, tier_rank_end: 50, prize_amount: pct['31-50'], display_order: 7, emoji: '⭐' },
        ];
        totalPrizePool = newCompetition.totalPrizePool; // Admin sets total; actual is 15% of revenue at runtime
      } else {
        // Legacy Top 10: dollar amount tiers
        prizeTiers = [
          { tier_name: '1st Place', tier_rank_start: 1, tier_rank_end: 1, prize_amount: newCompetition.prizes.first, display_order: 1, emoji: '🥇' },
          { tier_name: '2nd Place', tier_rank_start: 2, tier_rank_end: 2, prize_amount: newCompetition.prizes.second, display_order: 2, emoji: '🥈' },
          { tier_name: '3rd Place', tier_rank_start: 3, tier_rank_end: 3, prize_amount: newCompetition.prizes.third, display_order: 3, emoji: '🥉' },
          { tier_name: '4th Place', tier_rank_start: 4, tier_rank_end: 4, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 4, emoji: '🏆' },
          { tier_name: '5th Place', tier_rank_start: 5, tier_rank_end: 5, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 5, emoji: '🏆' },
          { tier_name: '6th Place', tier_rank_start: 6, tier_rank_end: 6, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 6, emoji: '🏆' },
          { tier_name: '7th Place', tier_rank_start: 7, tier_rank_end: 7, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 7, emoji: '🏆' },
          { tier_name: '8th Place', tier_rank_start: 8, tier_rank_end: 8, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 8, emoji: '🏆' },
          { tier_name: '9th Place', tier_rank_start: 9, tier_rank_end: 9, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 9, emoji: '🏆' },
          { tier_name: '10th Place', tier_rank_start: 10, tier_rank_end: 10, prize_amount: newCompetition.prizes.fourth_to_tenth, display_order: 10, emoji: '🏆' }
        ];
        totalPrizePool = newCompetition.prizes.first + newCompetition.prizes.second + newCompetition.prizes.third + (newCompetition.prizes.fourth_to_tenth * 7);
      }

      // Create competition + prize tiers + tier configs via API
      const result = await competitionService.createCompetitionFull({
        name: newCompetition.name,
        description: newCompetition.description,
        phaseId: newCompetition.phaseId,
        totalPrizePool,
        minimumQualification: newCompetition.minimumQualification,
        prizeTiers,
        tierConfigs: undefined,
      });

      if (!result.success) throw new Error('Failed to create competition');

      alert(`Competition created successfully for ${phase.phase_name}!`);
      setShowCreateForm(false);
      setNewCompetition({
        name: '',
        description: 'Build your network by referring new shareholders. Each qualified referral counts toward your ranking.',
        phaseId: 0,
        totalPrizePool: 150000,
        minimumQualification: 2500,
        prizes: {
          first: 60000,
          second: 30000,
          third: 18000,
          fourth_to_tenth: 6000
        },
        tierConfigs: [...DEFAULT_TIER_CONFIGS]
      });
      await loadData();

    } catch (error) {
      console.error('Error creating competition:', error);
      alert('Error creating competition. Please try again.');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">🏆 Phase Competition Management</h2>
        <div className="glass-card p-6">
          <div className="text-center py-8 text-white">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">🏆 Phase Competition Management</h2>
          <p className="text-white mt-1">
            Create and manage competitions for each share phase
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleSyncData}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
          >
            <span>{syncing ? '🔄' : '🔄'}</span>
            <span>{syncing ? 'Syncing...' : 'Sync Referral Data'}</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'phases', name: 'Phase Overview', icon: '📊' },
            { id: 'competitions', name: 'All Competitions', icon: '🏆' },
            { id: 'leaderboard', name: 'Leaderboard', icon: '🥇' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Competition Creation Form */}
      {showCreateForm && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Create New Competition</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Competition Name *
              </label>
              <input
                type="text"
                value={newCompetition.name}
                onChange={(e) => setNewCompetition(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                placeholder="Gold Diggers Club - Phase X"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Share Phase *
              </label>
              <select
                value={newCompetition.phaseId}
                onChange={(e) => {
                  const phaseId = parseInt(e.target.value);
                  const phase = investmentPhases.find(p => p.id === phaseId);
                  setNewCompetition(prev => ({
                    ...prev,
                    phaseId,
                    name: phase ? `Gold Diggers Club - ${phase.phase_name}` : prev.name
                  }));
                }}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
              >
                <option value={0}>Select Phase</option>
                {investmentPhases.map(phase => (
                  <option key={phase.id} value={phase.id}>
                    {phase.phase_name} (${phase.price_per_share}/share)
                    {phase.is_active ? ' - ACTIVE' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Total Prize Pool ($)
              </label>
              <input
                type="number"
                value={newCompetition.totalPrizePool}
                onChange={(e) => setNewCompetition(prev => ({ ...prev, totalPrizePool: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                min="0"
                step="1000"
              />
              {(() => {
                const sp = investmentPhases.find(p => p.id === newCompetition.phaseId);
                if (sp && sp.phase_number >= 5) return (
                  <p className="text-xs text-yellow-400 mt-1">Phase 5+: actual pool = 15% of revenue (calculated at runtime). This value is stored for reference only.</p>
                );
                return null;
              })()}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Minimum Qualification ($)
              </label>
              <input
                type="number"
                value={newCompetition.minimumQualification}
                onChange={(e) => setNewCompetition(prev => ({ ...prev, minimumQualification: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                min="0"
                step="500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={newCompetition.description}
                onChange={(e) => setNewCompetition(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                rows={3}
              />
            </div>

            {/* Prize Configuration — Phase 5+ shows Top 50 percentages, older phases show Top 10 dollar amounts */}
            {(() => {
              const selectedPhaseObj = investmentPhases.find(p => p.id === newCompetition.phaseId);
              const isPhase5Plus = selectedPhaseObj && selectedPhaseObj.phase_number >= 5;

              if (isPhase5Plus) {
                return (
                  <div className="md:col-span-2">
                    <div className="p-4 bg-gray-800/50 rounded-lg border border-green-600/30">
                      <h4 className="text-green-400 font-semibold mb-3">🏆 Top 50 Prize Distribution (Phase 5+)</h4>
                      <p className="text-sm text-white mb-3">
                        All 50 positions ranked by direct sales volume. No buyer qualifications — pure volume ranking.
                        Prize pool = 15% of total phase revenue (configurable after creation).
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="bg-yellow-900/30 border border-yellow-600/40 rounded p-2 text-center">
                          <div className="text-lg">🥇</div>
                          <div className="text-white font-bold">1st — 15%</div>
                        </div>
                        <div className="bg-gray-700/30 border border-gray-500/40 rounded p-2 text-center">
                          <div className="text-lg">🥈</div>
                          <div className="text-white font-bold">2nd — 12%</div>
                        </div>
                        <div className="bg-orange-900/30 border border-orange-600/40 rounded p-2 text-center">
                          <div className="text-lg">🥉</div>
                          <div className="text-white font-bold">3rd — 10%</div>
                        </div>
                        <div className="bg-blue-900/30 border border-blue-600/40 rounded p-2 text-center">
                          <div className="text-lg">🏆</div>
                          <div className="text-white font-bold">4th–10th — 3% ea</div>
                        </div>
                        <div className="bg-blue-900/20 border border-blue-600/30 rounded p-2 text-center">
                          <div className="text-lg">🏅</div>
                          <div className="text-white font-bold">11th–20th — 2% ea</div>
                        </div>
                        <div className="bg-blue-900/10 border border-blue-600/20 rounded p-2 text-center">
                          <div className="text-lg">🎖️</div>
                          <div className="text-white font-bold">21st–30th — 1% ea</div>
                        </div>
                        <div className="bg-gray-800/50 border border-gray-600/30 rounded p-2 text-center md:col-span-2">
                          <div className="text-lg">⭐</div>
                          <div className="text-white font-bold">31st–50th — 0.6% ea</div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-yellow-400">
                        Total: 15 + 12 + 10 + (7×3) + (10×2) + (10×1) + (20×0.6) = 100%
                      </div>
                    </div>
                  </div>
                );
              }

              // Legacy Phase ≤4: Top 10 dollar amount inputs
              return (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Prize Amounts (Top 10 Winners)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm text-white mb-1">🥇 1st Place ($)</label>
                      <input type="number" value={newCompetition.prizes.first}
                        onChange={(e) => setNewCompetition(prev => ({ ...prev, prizes: { ...prev.prizes, first: parseFloat(e.target.value) || 0 } }))}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" min="0" step="1000" />
                    </div>
                    <div>
                      <label className="block text-sm text-white mb-1">🥈 2nd Place ($)</label>
                      <input type="number" value={newCompetition.prizes.second}
                        onChange={(e) => setNewCompetition(prev => ({ ...prev, prizes: { ...prev.prizes, second: parseFloat(e.target.value) || 0 } }))}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" min="0" step="1000" />
                    </div>
                    <div>
                      <label className="block text-sm text-white mb-1">🥉 3rd Place ($)</label>
                      <input type="number" value={newCompetition.prizes.third}
                        onChange={(e) => setNewCompetition(prev => ({ ...prev, prizes: { ...prev.prizes, third: parseFloat(e.target.value) || 0 } }))}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" min="0" step="1000" />
                    </div>
                    <div>
                      <label className="block text-sm text-white mb-1">🏆 4th-10th Each ($)</label>
                      <input type="number" value={newCompetition.prizes.fourth_to_tenth}
                        onChange={(e) => setNewCompetition(prev => ({ ...prev, prizes: { ...prev.prizes, fourth_to_tenth: parseFloat(e.target.value) || 0 } }))}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" min="0" step="1000" />
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-white">
                    Total Prize Pool: ${(newCompetition.prizes.first + newCompetition.prizes.second + newCompetition.prizes.third + (newCompetition.prizes.fourth_to_tenth * 7)).toLocaleString()}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-300 border border-gray-600 rounded hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateCompetition}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Create Competition
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'phases' && (
        <div className="space-y-6">
          {/* Phase Overview Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {investmentPhases.map(phase => {
              const phaseCompetitions = competitions.filter(c => c.phase_id === phase.id);
              const activeCompetition = phaseCompetitions.find(c => c.is_active && c.status === 'active');

              return (
                <div key={phase.id} className="glass-card p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {phase.phase_name}
                      </h3>
                      <p className="text-white text-sm">
                        ${phase.price_per_share}/share
                      </p>
                      <p className="text-gray-500 text-sm">
                        {phase.shares_sold?.toLocaleString() || 0} / {phase.total_shares_available?.toLocaleString() || 0} shares sold
                      </p>
                    </div>
                    <div className={`px-2 py-1 rounded text-sm font-medium ${
                      phase.is_active
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-gray-500/20 text-white border border-gray-500/30'
                    }`}>
                      {phase.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </div>
                  </div>

                  {/* Competition Status */}
                  <div className="mb-4">
                    {activeCompetition ? (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-green-400 font-medium">🏆 Competition Active</span>
                          <span className="text-sm text-white">
                            {activeCompetition.total_participants || 0} participants
                          </span>
                        </div>
                        <p className="text-sm text-gray-300">
                          Prize Pool: {formatCurrency(activeCompetition.total_prize_pool)}
                        </p>
                        <p className="text-sm text-white">
                          Min Qualification: {formatCurrency(activeCompetition.minimum_qualification_amount)}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-3">
                        <span className="text-white">No active competition</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {activeCompetition ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedCompetition(activeCompetition);
                            setActiveTab('leaderboard');
                          }}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium"
                        >
                          View Leaderboard
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateForm(true);
                          }}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm"
                          title="Manage Competition"
                        >
                          ⚙️
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setNewCompetition(prev => ({
                            ...prev,
                            phaseId: phase.id,
                            name: `Gold Diggers Club - ${phase.phase_name}`,
                            prizes: {
                              first: 60000,
                              second: 30000,
                              third: 18000,
                              fourth_to_tenth: 6000
                            }
                          }));
                          setShowCreateForm(true);
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm font-medium"
                      >
                        Create Competition
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-white">{investmentPhases.length}</div>
              <div className="text-sm text-white">Total Phases</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-green-400">
                {investmentPhases.filter(p => p.is_active).length}
              </div>
              <div className="text-sm text-white">Active Phases</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-yellow-400">
                {competitions.filter(c => c.is_active && c.status === 'active').length}
              </div>
              <div className="text-sm text-white">Active Competitions</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-blue-400">
                {formatCurrency(competitions.reduce((sum, c) => sum + (c.total_prize_pool || 0), 0))}
              </div>
              <div className="text-sm text-white">Total Prize Pool</div>
            </div>
          </div>
        </div>
      )}

      {/* All Competitions Tab */}
      {activeTab === 'competitions' && (
        <div className="space-y-6">
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">All Competitions</h3>
            <div className="space-y-4">
              {competitions.length === 0 ? (
                <div className="text-center py-8 text-white">
                  No competitions found. Create competitions for your share phases.
                </div>
              ) : (
                competitions.map(competition => {
                  const phase = investmentPhases.find(p => p.id === competition.phase_id);
                  return (
                    <div key={competition.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-white">{competition.name}</h4>
                          <p className="text-sm text-white">
                            {phase ? `${phase.phase_name} (${formatCurrency(phase.price_per_share)}/share)` : 'Unknown Phase'}
                          </p>
                          <p className="text-sm text-gray-300 mt-1">
                            Prize Pool: {formatCurrency(competition.total_prize_pool)} |
                            Min Qualification: {formatCurrency(competition.minimum_qualification_amount)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`px-2 py-1 rounded text-sm font-medium ${
                            competition.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            competition.status === 'upcoming' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-white'
                          }`}>
                            {competition.status?.toUpperCase() || 'UNKNOWN'}
                          </div>
                          <button
                            onClick={() => {
                              setSelectedCompetition(competition);
                              setActiveTab('leaderboard');
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                          >
                            View
                          </button>
                          {phase && phase.phase_number >= 5 && (
                            <button
                              onClick={() => handleEditSinglePoolConfig(competition.id)}
                              className={`${editingSinglePoolId === competition.id ? 'bg-green-700' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1 rounded text-sm`}
                              title="Edit Single Pool Configuration"
                            >
                              {editingSinglePoolId === competition.id ? '✕ Close' : '⚙️ Top 50 Config'}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCompetition(competition.id, competition.name)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                            title="Delete Competition"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {/* Phase 5+: Single Pool Config Editor */}
                      {editingSinglePoolId === competition.id && phase && phase.phase_number >= 5 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <h5 className="text-green-400 font-semibold mb-2 text-sm">⚙️ Top 50 Prize Distribution Config</h5>
                          <div className="mb-3">
                            <label className="block text-sm text-gray-300 mb-1">Prize Pool % of Revenue</label>
                            <input type="number" value={singlePoolConfig.prize_pool_percent}
                              onChange={(e) => setSinglePoolConfig(prev => ({ ...prev, prize_pool_percent: parseFloat(e.target.value) || 15 }))}
                              className="w-32 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm" min="1" max="100" step="0.5" />
                            <span className="text-sm text-gray-400 ml-2">% of total phase revenue</span>
                          </div>
                          <div className="mb-3">
                            <label className="block text-sm text-gray-300 mb-1">Payout Percentages (must total 100%)</label>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                              {[
                                { key: 1, label: '🥇 1st Place' },
                                { key: 2, label: '🥈 2nd Place' },
                                { key: 3, label: '🥉 3rd Place' },
                              ].map(({ key, label }) => (
                                <div key={key}>
                                  <label className="text-gray-400 text-xs">{label}</label>
                                  <div className="flex items-center gap-1">
                                    <input type="number" value={singlePoolConfig.payout_percentages[key] ?? 0}
                                      onChange={(e) => setSinglePoolConfig(prev => ({ ...prev, payout_percentages: { ...prev.payout_percentages, [key]: parseFloat(e.target.value) || 0 } }))}
                                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm" min="0" max="100" step="0.1" />
                                    <span className="text-gray-500">%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                              {[
                                { key: '4-10', label: '4th–10th (×7)' },
                                { key: '11-20', label: '11th–20th (×10)' },
                                { key: '21-30', label: '21st–30th (×10)' },
                                { key: '31-50', label: '31st–50th (×20)' },
                              ].map(({ key, label }) => (
                                <div key={key}>
                                  <label className="text-gray-400 text-xs">{label}</label>
                                  <div className="flex items-center gap-1">
                                    <input type="number" value={singlePoolConfig.payout_percentages[key] ?? 0}
                                      onChange={(e) => setSinglePoolConfig(prev => ({ ...prev, payout_percentages: { ...prev.payout_percentages, [key]: parseFloat(e.target.value) || 0 } }))}
                                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm" min="0" max="100" step="0.1" />
                                    <span className="text-gray-500">% each</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="text-xs text-gray-400 mt-2">
                              Total: {(
                                (singlePoolConfig.payout_percentages[1] ?? 0) +
                                (singlePoolConfig.payout_percentages[2] ?? 0) +
                                (singlePoolConfig.payout_percentages[3] ?? 0) +
                                ((singlePoolConfig.payout_percentages['4-10'] ?? 0) * 7) +
                                ((singlePoolConfig.payout_percentages['11-20'] ?? 0) * 10) +
                                ((singlePoolConfig.payout_percentages['21-30'] ?? 0) * 10) +
                                ((singlePoolConfig.payout_percentages['31-50'] ?? 0) * 20)
                              ).toFixed(1)}%
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button onClick={handleSaveSinglePoolConfig} disabled={savingSinglePoolConfig}
                              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-1 rounded text-sm">
                              {savingSinglePoolConfig ? 'Saving...' : '💾 Save Config'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="space-y-6">
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              {selectedCompetition ? `${selectedCompetition.name} - Leaderboard` : 'Select a Competition'}
            </h3>
            {selectedCompetition ? (
              <div className="text-center py-8 text-white">
                Leaderboard functionality will be implemented here.
                <br />
                Competition: {selectedCompetition.name}
                <br />
                Prize Pool: {formatCurrency(selectedCompetition.total_prize_pool)}
              </div>
            ) : (
              <div className="text-center py-8 text-white">
                Please select a competition from the Phase Overview or All Competitions tab to view its leaderboard.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhaseCompetitionManager;
